<agpr-take-over-panel
    #takeOverPanel
    [id]="'agentSourcePanel'"
    class="agpr-agentSourcePanel"
    [header]="heading"
    headerIcon="fa-kit-duotone fa-agpr-ai-generic"
    [overlayContainer]="overlayContainer"
    [animationTrigger]="animationTrigger"
    [hideSlideOverlay]="hideSlideOverlay"
    [responsiveWidth]="true"
    [minPanelWidth]="960"
    [maxPanelWidth]="1400"
    [fullHeight]="true"
    [footerContext]="heading"
    (visibleChange)="onVisibleChange($event)"
>
    <div class="agpr-agentSourcePanel-body">
        <section class="agpr-agentSourcePanel-card">
            <h3 class="agpr-agentSourcePanel-heading">
                {{ 'AutomationAgents.adHoc.setUp' | localize }}
            </h3>

            <h4 class="agpr-agentSourcePanel-subHeading">
                {{ 'AutomationAgents.adHoc.sourceSelection' | localize }}
            </h4>

            <!-- The curation panel's own source row, so the picker, its filters and the view control
                 lay out the same way here -->
            <agpr-curation-agent-source-row
                [id]="'agentSourcePanel.source'"
                heading="AutomationAgents.adHoc.sourceCoverage"
                [source]="source"
                [showQuota]="false"
                [agentId]="agent?.id"
                (changed)="sourceChanged()"
            >
                <!-- A live agent watches from now on, so there is no range to pick -->
                @if (!isLiveAgent) {
                    <agpr-date-query-popup
                        [id]="'agentSourcePanel.dateRange'"
                        class="agpr-agentSourcePanel-dateRange"
                        [options]="dateRangeOptions"
                        [useDateSearchRangeSelection]="false"
                        [(ngModel)]="dateRange"
                        (ngModelChange)="dateRangeChanged($event)"
                    ></agpr-date-query-popup>
                }
            </agpr-curation-agent-source-row>

            <!-- A live agent's count is only a sample for the estimate, so it is not shown -->
            @if (!isLiveAgent) {
                <agpr-message
                    [id]="'agentSourcePanel.mentionCount'"
                    class="agpr-agentSourcePanel-count"
                    [severity]="messageSeverity.info"
                    [loading]="countingMentions"
                    [bottomMargin]="false"
                >
                    {{
                        'AutomationAgents.adHoc.mentionsSelected'
                            | localize: { count: mentionCount ?? ('AutomationAgents.notAvailable' | localize) }
                    }}
                </agpr-message>
            }

            <!-- Without the banner above, the gap it left has to be kept here -->
            <h4
                class="agpr-agentSourcePanel-subHeading"
                [class.agpr-agentSourcePanel-subHeading--spaced]="isLiveAgent"
            >
                {{ 'AutomationAgents.adHoc.agentSelection' | localize }}
            </h4>

            <agpr-dropdown
                [id]="'agentSourcePanel.type'"
                label="AutomationAgents.adHoc.type"
                [options]="agentTypeOptions"
                [bindToValue]="true"
                [(ngModel)]="agentType"
            ></agpr-dropdown>

            <div class="agpr-inputGroupRow agpr-agentSourcePanel-agentRow">
                <div class="agpr-inputGroupRow-dropdown">
                    <agpr-dropdown
                        [id]="'agentSourcePanel.agent'"
                        label="AutomationAgents.adHoc.agent"
                        placeholder="AutomationAgents.adHoc.agent.placeholder"
                        [options]="agentOptions"
                        [bindToValue]="true"
                        [ngModel]="selectedAgentId"
                        (ngModelChange)="agentSelected($event)"
                    ></agpr-dropdown>
                </div>
                <div class="agpr-inputGroupRow-buttons">
                    <agpr-button
                        [id]="'agentSourcePanel.manageAgents'"
                        [style]="buttonTypes.block"
                        icon="fa-regular fa-duotone fa-gear"
                        tooltip="AutomationAgents.agent.manageAgents"
                        tooltipPosition="top"
                        [ariaLabel]="'AutomationAgents.agent.manageAgents' | localize"
                        [routerLink]="'/Agents'"
                        (click)="cancel()"
                    ></agpr-button>
                </div>
            </div>
        </section>

        <section class="agpr-agentSourcePanel-card agpr-agentSourcePanel-card--quota">
            <h3 class="agpr-agentSourcePanel-heading">
                {{ quotaHeading | localize }}
            </h3>

            <!-- A live agent's figures are a projection, which is worth saying before they are read -->
            @if (isLiveAgent) {
                <agpr-message
                    [id]="'agentSourcePanel.estimateDisclaimer'"
                    class="agpr-agentSourcePanel-disclaimer"
                    [severity]="messageSeverity.info"
                    [bottomMargin]="false"
                >
                    {{ 'AutomationAgents.liveAgent.quotaUsage.disclaimer' | localize }}
                </agpr-message>
            }

            @if (hasEstimate) {
                <div class="agpr-agentSourcePanel-quotaCard">
                    <h4 class="agpr-agentSourcePanel-quotaHeading">
                        {{ usageHeading | localize }}
                    </h4>
                    <p class="agpr-agentSourcePanel-quotaCount">{{ estimatedUsage | number }}</p>
                </div>
            }

            <!-- The pool is only known once it has been configured; without one there is nothing
                 to measure the run against -->
            @if (hasEstimate && quotaTotal > 0) {
                <div class="agpr-agentSourcePanel-quotaCard">
                    <h4 class="agpr-agentSourcePanel-quotaHeading">
                        {{ 'AutomationAgents.adHoc.quotaUsage.relative' | localize }}
                    </h4>

                    <!-- Each segment reports itself on hover, which is where the figures live -->
                    <div class="agpr-agentSourcePanel-quotaBar">
                        <span
                            class="agpr-agentSourcePanel-quotaSegment agpr-agentSourcePanel-quotaSegment--used"
                            [style.width.%]="quotaPercent(quotaUsed)"
                            [agprTooltip]="quotaTooltip('AutomationAgents.adHoc.quotaUsage.totalUsed', quotaUsed, quotaColors.used)"
                            [tooltipAllowsHtml]="true"
                            tooltipPosition="top"
                        ></span>
                        <span
                            class="agpr-agentSourcePanel-quotaSegment agpr-agentSourcePanel-quotaSegment--estimate"
                            [style.width.%]="quotaPercent(estimatedUsage)"
                            [agprTooltip]="quotaTooltip('AutomationAgents.adHoc.quotaUsage.agentUsage', estimatedUsage, quotaColors.estimate)"
                            [tooltipAllowsHtml]="true"
                            tooltipPosition="top"
                        ></span>
                        <span
                            class="agpr-agentSourcePanel-quotaSegment agpr-agentSourcePanel-quotaSegment--overall"
                            [style.width.%]="quotaPercent(overallEstimate)"
                            [agprTooltip]="quotaTooltip('AutomationAgents.adHoc.quotaUsage.overall', overallEstimate, quotaColors.overall)"
                            [tooltipAllowsHtml]="true"
                            tooltipPosition="top"
                        ></span>
                        <span
                            class="agpr-agentSourcePanel-quotaSegment agpr-agentSourcePanel-quotaSegment--remaining"
                            [style.width.%]="quotaPercent(quotaRemaining)"
                            [agprTooltip]="quotaTooltip('AutomationAgents.adHoc.quotaUsage.remaining', quotaRemaining, quotaColors.track)"
                            [tooltipAllowsHtml]="true"
                            tooltipPosition="top"
                        ></span>
                    </div>
                    <p class="agpr-agentSourcePanel-quotaTotal">{{ quotaTotal | number }}</p>

                    <ul class="agpr-agentSourcePanel-quotaLegend">
                        <li class="agpr-agentSourcePanel-quotaLegendItem">
                            <span class="agpr-agentSourcePanel-quotaSwatch agpr-agentSourcePanel-quotaSwatch--used"></span>
                            {{ 'AutomationAgents.adHoc.quotaUsage.totalUsed' | localize }}
                        </li>
                        <li class="agpr-agentSourcePanel-quotaLegendItem">
                            <span class="agpr-agentSourcePanel-quotaSwatch agpr-agentSourcePanel-quotaSwatch--overall"></span>
                            {{ 'AutomationAgents.adHoc.quotaUsage.overall' | localize }}
                        </li>
                        <li class="agpr-agentSourcePanel-quotaLegendItem">
                            <span class="agpr-agentSourcePanel-quotaSwatch agpr-agentSourcePanel-quotaSwatch--estimate"></span>
                            {{ 'AutomationAgents.adHoc.quotaUsage.agentUsage' | localize }}
                        </li>
                        <li class="agpr-agentSourcePanel-quotaLegendItem">
                            <span class="agpr-agentSourcePanel-quotaSwatch agpr-agentSourcePanel-quotaSwatch--remaining"></span>
                            {{ 'AutomationAgents.adHoc.quotaUsage.remaining' | localize }}
                        </li>
                    </ul>

                    @if (exceedsQuota) {
                        <agpr-message
                            [id]="'agentSourcePanel.quotaExceeds'"
                            class="agpr-agentSourcePanel-quotaWarning"
                            [severity]="messageSeverity.warning"
                            [bottomMargin]="false"
                        >
                            {{ 'AutomationAgents.adHoc.quotaUsage.exceeds' | localize }}
                        </agpr-message>
                    }
                </div>
            } @else {
                <agpr-message
                    [id]="'agentSourcePanel.quotaEstimate'"
                    [severity]="messageSeverity.noContent"
                    [bottomMargin]="false"
                >
                    {{ 'AutomationAgents.adHoc.quotaUsage.unavailable' | localize }}
                </agpr-message>
            }

            @if (hasEstimate) {
                <div class="agpr-agentSourcePanel-upsell">
                    <span class="fa-kit-duotone fa-agpr-ai-generic agpr-agentSourcePanel-upsellIcon" aria-hidden="true"></span>
                    <span [innerHTML]="'AutomationAgents.usage.needMore' | localize"></span>
                </div>
            }
        </section>
    </div>

    <ng-template #takeOverFooterLeft>
        <agpr-button
            [id]="'agentSourcePanel.cancel'"
            label="Core.button.cancel"
            [style]="buttonTypes.link"
            (click)="cancel()"
        ></agpr-button>
    </ng-template>

    <ng-template #takeOverFooterRight>
        <agpr-button
            [id]="'agentSourcePanel.run'"
            [label]="submitLabel"
            [style]="buttonTypes.primary"
            [disabled]="!canRun"
            [loading]="running"
            (click)="submit()"
        ></agpr-button>
    </ng-template>
</agpr-take-over-panel>
